import type { SavedItem } from '@nelnela/contracts';

/**
 * Saving, as the reading surfaces see it (M6e ROADMAP §3 and §4).
 *
 * Two things live here because they are the same fact seen twice:
 *
 *  - **Is this already saved?** One learner's saves, keyed by (selection, sentence). The gloss
 *    sheet reads that membership so its Save action can render as an inert "Saved ✓". It is NOT
 *    a flag on the gloss: a gloss row is cached and memoised across learners, and saved-ness is
 *    one learner's fact — putting it in the gloss would show one person's saves to everyone.
 *  - **What does this tap mean?** A second tap on the same word saves it. The decision is a pure
 *    function of two timestamps and two keys, in this module rather than in the screen, because
 *    the web suite runs under node with no DOM (`lib/sw.ts` is the house pattern) and a gesture
 *    that can only be tested by hand is a gesture nobody tests.
 *
 * ⟂ SPEC DEPARTURE, RECORDED NOT EDITED: `docs/design/reading-surface-spec.md` says saving is a
 * row-＋ affordance, not a text gesture. The spec is a protected artifact and is not being edited;
 * ROADMAP § M6e §4 is the citation for the departure. The ＋ still works and still means the same
 * thing — this adds a second way in, on the word itself.
 */

/**
 * The identity of a save on a reading surface: what was selected, inside what sentence.
 *
 * Trimmed, because the two sides are produced differently — `selectionText` strips edge
 * punctuation off a tapped token, and a `saved_item.sentence` is whatever was denormalised at
 * save time — and an invisible space either side is not a different save.
 *
 * Joined on \u0000 (the house separator — `lib/grader.ts`'s `key`, both gloss memos) rather than
 * the space the handoff sketched: a phrase save puts spaces in the FIRST half, so
 * ('ის სახლში', 'X') and ('ის', 'სახლში X') would otherwise be the same key.
 */
export const savedKey = (selection: string, sentence: string): string =>
  `${selection.trim()}\u0000${sentence.trim()}`;

/** The membership set the gloss sheet reads, from one scoped page of `savedList`. */
export function savedKeys(
  items: readonly Pick<SavedItem, 'selection' | 'sentence'>[] | undefined,
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const i of items ?? []) out.add(savedKey(i.selection, i.sentence));
  return out;
}

/**
 * A click that arrives this soon after a swipe ended is the swipe's own tail, and the transcript
 * swallows it (`components/watch/Transcript.tsx`, which imports this).
 *
 * It lives here rather than there because `SAVE_TAP_WINDOW_MS` below has to be stated against it,
 * and two constants that must agree should not be in two files.
 */
export const SWIPE_CLICK_GUARD_MS = 400;

/**
 * How long a word stays armed after it was tapped: tap it again inside this and it is saved.
 *
 * Longer than the swipe guard, and that is the whole reason the two constants share a file. A tap
 * inside the guard is never delivered at all, so a window SHORTER than the guard would have a band
 * of timings in which the surface swallowed the tap while the first one had already expired —
 * the learner taps twice, nothing happens, and nothing anywhere says why. Longer than the guard,
 * every tap the surface actually delivers is still inside the window.
 *
 * And bounded, rather than "until the sheet closes": an armed word that never disarms turns an
 * idle re-tap minutes later into a silent save.
 */
export const SAVE_TAP_WINDOW_MS = 1200;

/** The last word tap this surface delivered — `null` before the first, or after anything that
 *  changed what the sheet is showing (a swipe, a nudge, a phrase). */
export interface ArmedTap {
  key: string;
  at: number;
}

/**
 * What a word tap means: 'save' only for a second tap on the SAME word, inside the window, on
 * something not already saved.
 *
 * Every other case is 'look-up' — the behaviour that was there before M6e, unchanged, including
 * the one that matters most: **a tap on a different word is never a save**, so Watch's
 * tap-to-extend (`resolveTap`) still sees exactly the taps it saw before.
 *
 * `alreadySaved` is not a nicety. `savedCreate` inserts a row every time it is called, so a
 * double-tap on a word the learner already kept would quietly produce a second identical entry in
 * their Saved list. A tap on an already-saved word re-opens its gloss, which is where the sheet
 * says "Saved ✓".
 */
export function decideWordTap(
  armed: ArmedTap | null,
  tap: ArmedTap,
  opts: { alreadySaved?: boolean; windowMs?: number } = {},
): 'save' | 'look-up' {
  if (opts.alreadySaved === true) return 'look-up';
  if (armed === null || armed.key !== tap.key) return 'look-up';
  const dt = tap.at - armed.at;
  // A clock that went backwards (a device clock change mid-session) is not a double tap.
  if (dt < 0) return 'look-up';
  return dt <= (opts.windowMs ?? SAVE_TAP_WINDOW_MS) ? 'save' : 'look-up';
}
